import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  CheckCircle2, Circle, Clock, AlertCircle, ChevronRight, PartyPopper,
} from 'lucide-react';
import { toast } from 'sonner';
import { onboardingAPI, SECTION_STATUS_META } from '../lib/onboardingAPI';
import { EsignOnboardingCard } from '../components/esign/EsignOnboardingCard';
import { IdentityStatusCard } from '../components/onboarding/IdentityStatusCard';
import { ClientAuthorityCard } from '../components/clearance/ClientAuthorityCard';
import { PortalNominationCard } from '../components/nomination/PortalNominationCard';

const statusIcon = (status) => {
  switch (status) {
    case 'completed': return <CheckCircle2 className="w-5 h-5 text-green-500" />;
    case 'in_progress': return <Clock className="w-5 h-5 text-amber-500" />;
    case 'needs_review': return <AlertCircle className="w-5 h-5 text-orange-500" />;
    default: return <Circle className="w-5 h-5 text-muted-foreground/50" />;
  }
};

export default function OnboardingChecklist() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await onboardingAPI.myChecklist();
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load your onboarding');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="p-6"><div className="animate-pulse h-64 bg-muted rounded" /></div>;
  }
  if (!data) return null;

  const submitted = data.client?.onboarding_submitted;
  const updatedAfterSubmit = data.client?.updated_after_submit;
  const approved = data.client?.status === 'active';
  const visibleSections = data.sections.filter((s) => s.applicable);

  const openSection = (s) => {
    if (s.terminal) { navigate('/onboarding/review'); return; }
    navigate(`/onboarding/section/${s.key}`);
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="onboarding-checklist-title">
          New Client Onboarding
        </h1>
        <p className="text-muted-foreground mt-1">
          Complete each section at your own pace — your progress is saved automatically, so you can return any time.
        </p>
      </div>

      {submitted && !updatedAfterSubmit && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-5 flex items-center gap-3">
            <PartyPopper className="w-6 h-6 text-green-600" />
            <div>
              <p className="font-semibold text-green-800">
                {approved ? 'Onboarding approved' : 'Onboarding submitted — awaiting approval'}
              </p>
              <p className="text-sm text-green-700">
                {approved
                  ? 'Your account is fully set up — your portal (jobs, documents, messages and more) is now unlocked in the menu.'
                  : 'Your details have been submitted to your accountant for review. Once they approve, your account will be created and the rest of your portal (jobs, documents, messages, deadlines) will unlock. You can still update any section below and re-submit in the meantime.'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {submitted && <EsignOnboardingCard />}

      {submitted && updatedAfterSubmit && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-5 flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-amber-600" />
            <div>
              <p className="font-semibold text-amber-800">You have unsubmitted changes</p>
              <p className="text-sm text-amber-700">You've edited your details since submitting. Use <strong>Review &amp; Re-submit</strong> below so your accountant reviews the latest version.</p>
            </div>
          </CardContent>
        </Card>
      )}

      <IdentityStatusCard data={data} />
      <ClientAuthorityCard />
      <PortalNominationCard />

      {/* Progress */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground/90">Overall progress</span>
            <span className="text-sm font-semibold text-foreground">{data.completion_pct}%</span>
          </div>
          <Progress value={data.completion_pct} className="h-2" />
          <p className="text-xs text-muted-foreground mt-2">
            {data.completed_sections} of {data.total_sections} sections completed
          </p>
        </CardContent>
      </Card>

      {/* Sections */}
      <div className="space-y-3">
        {visibleSections.map((s) => {
          const meta = SECTION_STATUS_META[s.status] || SECTION_STATUS_META.not_started;
          return (
            <Card
              key={s.key}
              className="transition hover:shadow-md cursor-pointer"
              onClick={() => openSection(s)}
              data-testid={`onboarding-section-${s.key}`}
            >
              <CardContent className="py-4 flex items-center gap-4">
                {statusIcon(s.status)}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-foreground">{s.title}</span>
                    {s.required && <span className="text-xs text-red-500">required</span>}
                    {s.conditional && <Badge className="bg-muted text-muted-foreground text-[10px]">conditional</Badge>}
                  </div>
                </div>
                <Badge className={meta.color}>{meta.label}</Badge>
                <ChevronRight className="w-5 h-5 text-muted-foreground/50" />
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Review & submit / re-submit CTA */}
      <div className="flex justify-end">
        <Button
          onClick={() => navigate('/onboarding/review')}
          disabled={!data.can_submit}
          data-testid="onboarding-review-cta"
        >
          {submitted
            ? (updatedAfterSubmit ? 'Review & Re-submit' : 'Review submission')
            : 'Review & Submit'} <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
      {!data.can_submit && (
        <p className="text-right text-xs text-muted-foreground">
          {data.identity_ok ? 'Complete the required sections to submit.' : 'Please complete identity verification before submitting.'}
        </p>
      )}
    </div>
  );
}
